import axios, { AxiosInstance, Method } from 'axios';

import {
  IHttpClient,
  IHttpRequestConfig,
  IPaginatedResponse,
} from '@/infra/interfaces/http/http-client';
import { onRequest, onRequestError } from '@/infra/network/axios/interceptors/request';
import { onResponse, onResponseError } from '@/infra/network/axios/interceptors/response';

export class AxiosAdapter implements IHttpClient {
  private readonly client: AxiosInstance;

  constructor(baseUrl?: string, xApiKey?: string) {
    this.client = axios.create({
      baseURL: baseUrl,
      headers: { 'x-api-key': xApiKey },
    });

    this.client.interceptors.request.use(onRequest, onRequestError);
    this.client.interceptors.response.use(onResponse, onResponseError(this.client));
  }

  public async request<T = unknown, Pagination extends boolean | undefined = undefined>(
    config: IHttpRequestConfig<Pagination>,
  ): Promise<T> {
    return await this.client.request(config);
  }

  private async send<T = unknown, P extends boolean | undefined = undefined>(
    method: Method,
    url: string,
    data?: unknown,
    config?: IHttpRequestConfig<P>,
  ): Promise<T> {
    return await this.client.request({ method, url, data, ...config });
  }

  async get<T = unknown, Pagination extends boolean | undefined = undefined>(
    url: string,
    config?: IHttpRequestConfig<Pagination>,
  ): Promise<Pagination extends true ? IPaginatedResponse<T> : T> {
    return await this.send('get', url, undefined, config);
  }
  async post<T = unknown, P extends boolean | undefined = undefined>(
    url: string,
    data?: unknown,
    config?: IHttpRequestConfig<P>,
  ): Promise<T> {
    return await this.send('post', url, data, config);
  }
  async put<T = unknown, P extends boolean | undefined = undefined>(
    url: string,
    data?: unknown,
    config?: IHttpRequestConfig<P>,
  ): Promise<T> {
    return await this.send('put', url, data, config);
  }

  async patch<T = unknown, P extends boolean | undefined = undefined>(
    url: string,
    data?: unknown,
    config?: IHttpRequestConfig<P>,
  ): Promise<T> {
    return await this.send('patch', url, data, config);
  }

  async head<T = unknown, P extends boolean | undefined = undefined>(
    url: string,
    config?: IHttpRequestConfig<P>,
  ): Promise<T> {
    return await this.send('head', url, undefined, config);
  }

  async options<T = unknown, P extends boolean | undefined = undefined>(
    url: string,
    config?: IHttpRequestConfig<P>,
  ): Promise<T> {
    return await this.send('options', url, undefined, config);
  }

  async delete<T = unknown, P extends boolean | undefined = undefined>(
    url: string,
    config?: IHttpRequestConfig<P>,
  ): Promise<T> {
    return await this.send('delete', url, undefined, config);
  }
}
